import { Menu } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  fetchOrganizationIntegrationSettings,
  toggleEnableCategoryIntegrations,
} from "../../../../api-client/organization/OrganizationIntegrationSettingsAPIClient";
import {
  APICategory,
  UnreleasedAPICategory,
  OrganizationIntegrationSettings,
  UserType,
} from "../../../../models/Entities";
import { SectionHeaderWrapper } from "../../../shared/SectionHeaderWrapper";
import { showErrorToast, showSuccessToast } from "../../../shared/Toasts";
import ConfigurationIntegrationsList from "./ConfigurationIntegrationsList";
import useAppContext from "../../../context/useAppContext";
import {
  Accordion,
  Button,
  ButtonVariant,
  Link,
  SkeletonLoader,
  Toggle,
  Tooltip,
} from "@merge-api/merge-javascript-shared";
import checkCategoryBetaPublicStatusAndRenderCategoryTitle from "../../utils/checkCategoryBetaPublicStatusAndRenderCategoryTitle";
import ConfigurationIntegrationsPageWrapper from "./ConfigurationIntegrationsPageWrapper";
import SyncFrequencyUpsellModal from "./ConfigurationIntegrationSyncFrequencyUpsellModal";

const INTEGRATION_MANAGEMENT_ENABLED_ROLES = new Set<UserType>([
  UserType.admin_with_billing,
  UserType.admin_without_billing,
  UserType.member,
]);

type Props = {
  category: APICategory | UnreleasedAPICategory;
  isEndpointUsageEnabled: boolean;
};

const ConfigurationEnabledIntegrationsPage = ({ category, isEndpointUsageEnabled }: Props) => {
  const [organizationIntegrationsSettings, setOrganizationIntegrationsSettings] = useState<
    Array<OrganizationIntegrationSettings> | undefined
  >();

  const { user } = useAppContext();
  const canManageIntegrations = INTEGRATION_MANAGEMENT_ENABLED_ROLES.has(user.type);

  const [showUpsellModal, setShowUpsellModal] = useState(false);

  useEffect(() => {
    fetchOrganizationIntegrationSettings({
      onlyEnabled: false,
      onFetch: (data) => {
        setOrganizationIntegrationsSettings(data);
      },
    });
  }, []);

  const integrationSkeletonRows = (
    <div className="flex flex-col gap-y-5">
      {Array.from({ length: 12 }, (_, i) => (
        <Accordion
          key={i}
          title={
            <div className="flex flex-row items-center gap-x-4 w-full pr-6">
              <Menu size={16} className="text-gray-40" />
              <SkeletonLoader height={40} width={40} borderRadius={999} />
              <div className="flex flex-col gap-y-0.5 flex-1 min-w-0">
                <SkeletonLoader height={24} width={140} borderRadius={8} />
                <SkeletonLoader height={20} width={98} borderRadius={8} />
              </div>
              <Toggle checked={false} disabled label="Disabled" onChange={() => {}} />
            </div>
          }
          titleClassName="px-6 py-4 rounded-lg"
          chevronOrientation="right"
          expanded={false}
        >
          {null}
        </Accordion>
      ))}
    </div>
  );

  function enableCategoryIntegrations(is_enabled: boolean) {
    toggleEnableCategoryIntegrations({
      category: category,
      isEnabled: is_enabled,
      onToggle: (data: Array<OrganizationIntegrationSettings>) => {
        showSuccessToast(
          "Successfully " +
            (is_enabled
              ? "enabled all integrations!"
              : "disabled all integrations without a Linked Account!"),
        );
        setOrganizationIntegrationsSettings(data);
      },
      onError: () => {
        showErrorToast(
          "Unable to " +
            (is_enabled
              ? "enabled all integrations."
              : "disabled all integrations without a Linked Account."),
        );
      },
    });
  }

  return (
    <>
      <SyncFrequencyUpsellModal
        showUpsellModal={showUpsellModal}
        setShowUpsellModal={setShowUpsellModal}
      />

      <ConfigurationIntegrationsPageWrapper isEndpointUsageEnabled={isEndpointUsageEnabled}>
        <div className={showUpsellModal ? "opacity-50" : "opacity-100"}>
          <SectionHeaderWrapper
            title={checkCategoryBetaPublicStatusAndRenderCategoryTitle(
              category as APICategory | UnreleasedAPICategory,
            )}
            subtitle={
              <>
                See which fields are supported and compare coverage across integrations in our{" "}
                <Link
                  href={`https://docs.merge.dev/integrations/${
                    category ?? "hris"
                  }/supported-fields`}
                  target="_blank"
                >
                  Docs
                </Link>
              </>
            }
            buttons={
              canManageIntegrations ? (
                <div className="flex flex-row items-center gap-x-3">
                  <Tooltip title="Only disables integrations without active Linked Accounts">
                    <Button
                      variant={ButtonVariant.SecondaryGray}
                      size="sm"
                      className="mr-2"
                      onClick={() => enableCategoryIntegrations(false)}
                    >
                      Disable all
                    </Button>
                  </Tooltip>
                  <Button
                    variant={ButtonVariant.PrimaryGray}
                    size="sm"
                    onClick={() => enableCategoryIntegrations(true)}
                  >
                    Enable all
                  </Button>
                </div>
              ) : undefined
            }
          >
            <div className="flex flex-col">
              {organizationIntegrationsSettings ? (
                <ConfigurationIntegrationsList
                  key={category}
                  category={category}
                  organizationIntegrationsSettings={organizationIntegrationsSettings}
                />
              ) : (
                integrationSkeletonRows
              )}
            </div>
          </SectionHeaderWrapper>
        </div>
      </ConfigurationIntegrationsPageWrapper>
    </>
  );
};

export default ConfigurationEnabledIntegrationsPage;
